import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { DashboardSkeleton } from './SkeletonLoaders';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Typography, TableSortLabel, ToggleButton, ToggleButtonGroup,
  CircularProgress, Grid, Card, CardContent, FormControl, Select, MenuItem,
  LinearProgress, Chip, useMediaQuery, useTheme, Stack, Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

// Helper function to format dates as MM-DD-YYYY
const formatDateDisplay = (dateString) => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${month}-${day}-${year}`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { selectedBuilding } = useContext(AuthContext);
  const [locations, setLocations] = useState([]);
  const [filteredIndividuals, setFilteredIndividuals] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'days_since_report', direction: 'descending' });
  const [filterName, setFilterName] = useState('');
  const [filterReport, setFilterReport] = useState('');
  const [filteredDays, setFilteredDays] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchData = async () => {
      try {        
        setIsLoading(true);
        
        // Fetch overdue forms from the new yearly system
        const buildingParam = selectedBuilding && selectedBuilding.id !== 4 
          ? `?building_location_id=${selectedBuilding.id}` 
          : '';
        
        const response = await fetchWithAuth(`/api/forms/overdue${buildingParam}`);
        
        if (response.ok) {
          const data = await response.json();
          setReportData(data);
        } else {
          console.error('Failed to fetch overdue forms:', response.statusText);
          // Fallback to empty array
          setReportData([]);
        }
        
        // For backwards compatibility, keep some sample data structure
        const sampleData = [
          { id: '21', individual_id: '1', report_type_id: '1', report_name: 'IP Held', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2025-02-20' },
          { id: '22', individual_id: '2', report_type_id: '2', report_name: 'IP Sign-In', individual_name: 'Bob White', building_location_name: 'The Victorian', report_date: '2025-02-20' },
          { id: '23', individual_id: '3', report_type_id: '3', report_name: 'LON', individual_name: 'Catherine Black', building_location_name: 'Puffins', report_date: '2025-02-20' },
          { id: '24', individual_id: '4', report_type_id: '4', report_name: 'Supports and Services', individual_name: 'David Blue', building_location_name: 'All', report_date: '2025-02-20' },
          { id: '25', individual_id: '5', report_type_id: '5', report_name: 'EFS', individual_name: 'Emma Purple', building_location_name: 'Puffins', report_date: '2025-02-20' },
          { id: '26', individual_id: '6', report_type_id: '1', report_name: 'IP Held', individual_name: 'Frank Orange', building_location_name: 'All', report_date: '2025-02-20' },
          { id: '27', individual_id: '6', report_type_id: '2', report_name: 'IP Sign-In', individual_name: 'Frank Orange', building_location_name: 'All', report_date: '2025-01-07' },
          { id: '28', individual_id: '9', report_type_id: '3', report_name: 'LON', individual_name: 'Henry Red', building_location_name: 'The Victorian', report_date: '2025-01-12' },
          { id: '29', individual_id: '10', report_type_id: '4', report_name: 'Supports and Services', individual_name: 'Isabel Brown', building_location_name: 'All', report_date: '2025-01-27' },
          { id: '30', individual_id: '8', report_type_id: '5', report_name: 'EFS', individual_name: 'Grace Yellow', building_location_name: 'All', report_date: '2024-11-23' },
          { id: '41', individual_id: '1', report_type_id: '2', report_name: 'IP Sign-In', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2024-11-23' },
          { id: '51', individual_id: '1', report_type_id: '3', report_name: 'LON', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2024-12-29' },
          { id: '61', individual_id: '1', report_type_id: '4', report_name: 'Supports and Services', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2024-12-23' },
          { id: '71', individual_id: '1', report_type_id: '5', report_name: 'EFS', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2024-12-09' },
          { id: '81', individual_id: '1', report_type_id: '6', report_name: 'Intake', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2025-01-04' },
          { id: '91', individual_id: '1', report_type_id: '7', report_name: 'Consent', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2025-02-07' },
          { id: '101', individual_id: '1', report_type_id: '8', report_name: 'Medical Waiver', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2024-12-08' },
          { id: '111', individual_id: '1', report_type_id: '9', report_name: 'Behavior Support Plan (BSP)', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2024-11-30' },
          { id: '121', individual_id: '1', report_type_id: '10', report_name: 'Individual Program Plan (IPP)', individual_name: 'Alice Green', building_location_name: 'The Victorian', report_date: '2025-01-29' },
          { id: '131', individual_id: '2', report_type_id: '1', report_name: 'IP Held', individual_name: 'Bob White', building_location_name: 'The Victorian', report_date: '2025-02-11' },
          { id: '151', individual_id: '2', report_type_id: '3', report_name: 'LON', individual_name: 'Bob White', building_location_name: 'The Victorian', report_date: '2025-02-10' },
          { id: '161', individual_id: '2', report_type_id: '4', report_name: 'Supports and Services', individual_name: 'Bob White', building_location_name: 'The Victorian', report_date: '2025-01-15' },
          { id: '171', individual_id: '2', report_type_id: '5', report_name: 'EFS', individual_name: 'Bob White', building_location_name: 'The Victorian', report_date: '2024-11-24' },
          { id: '181', individual_id: '2', report_type_id: '6', report_name: 'Intake', individual_name: 'Bob White', building_location_name: 'The Victorian', report_date: '2025-01-22' },
          { id: '191', individual_id: '2', report_type_id: '7', report_name: 'Consent', individual_name: 'Bob White', building_location_name: 'The Victorian', report_date: '2025-01-16' },
          { id: '201', individual_id: '2', report_type_id: '8', report_name: 'Medical Waiver', individual_name: 'Bob White', building_location_name: 'The Victorian', report_date: '2024-12-12' }
        ];
        
        // Set locations
        setLocations([
          { id: 0, name: "All" },
          { id: 1, name: "Riverview Farm" },
          { id: 2, name: "The Victorian" },
          { id: 3, name: "Puffins" }
        ]);
        
        // Sample data is no longer used - real data comes from API above
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [selectedBuilding]);

  // Function to parse dates correctly
  const parseDate = (dateString) => {
    if (!dateString) return null;
    
    // Handle date in format YYYY-MM-DD
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    
    // Fallback to standard date parsing
    return new Date(dateString);
  };

  // Function to calculate days since report
  const calculateDaysSinceReport = (reportDate) => {
    if (!reportDate) return 0;
    
    const today = new Date();
    const reportDateObj = parseDate(reportDate);
    
    if (!reportDateObj) return 0;
    
    // Calculate difference in days
    const diffTime = today - reportDateObj;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  // Process report data to ensure days_since_report is correctly calculated
  const processedReportData = React.useMemo(() => {
    return reportData.map(report => ({
      ...report,
      days_since_report: report.days_overdue !== null && report.days_overdue !== undefined
        ? report.days_overdue
        : (report.days_since_report !== null && report.days_since_report !== undefined
            ? report.days_since_report
            : calculateDaysSinceReport(report.due_date || report.report_date))
    }));
  }, [reportData]);

  // Apply filters and sorting
  const filteredReports = React.useMemo(() => {
    let filtered = [...processedReportData];
    
    // First apply building location filter based on AuthContext's selectedBuilding
    if (selectedBuilding && selectedBuilding.name !== "All") {
      filtered = filtered.filter(report => 
        report.building_location_name === selectedBuilding.name
      );
    }
    
    // Apply day range filter
    if (filteredDays !== "all") {
      filtered = filtered.filter(report => {
        if (filteredDays === 90) return report.days_since_report >= 90;
        if (filteredDays === 60) return report.days_since_report >= 60 && report.days_since_report < 90;
        if (filteredDays === 45) return report.days_since_report >= 45 && report.days_since_report < 60;
        if (filteredDays === 30) return report.days_since_report < 45;
        
        return true;
      });
    }
    
    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(report => 
        report.individual_name && report.individual_name.toLowerCase().includes(filterName.toLowerCase())
      );
    }
    
    // Apply report filter
    if (filterReport) {
      filtered = filtered.filter(report => 
        report.report_name && report.report_name.toLowerCase().includes(filterReport.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [reportData, selectedBuilding, filteredDays, filterName, filterReport, sortConfig]);
  
  // Get status color based on days since report
  const getStatusColor = (report) => {
    const days = typeof report === 'object' 
      ? report.days_since_report 
      : report;
      
    if (days >= 90) return '#F55F5F'; // Red
    if (days >= 60) return '#E0E000'; // Yellow
    if (days >= 45) return 'lightblue'; // Blue
    return 'lightgreen'; // Green
  };
  
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const handleFilterChange = (event, newValue) => {
    if (newValue !== null) {
      setFilteredDays(newValue);
    }
  };
  
  // Dashboard stats - filter by selected building from AuthContext if applicable
  const buildingFilteredReports = React.useMemo(() => {
    if (selectedBuilding && selectedBuilding.name !== "All") {
      return processedReportData.filter(r => r.building_location_name === selectedBuilding.name);
    }
    return processedReportData;
  }, [processedReportData, selectedBuilding]);
  
  const redReports = buildingFilteredReports.filter(r => r.days_since_report >= 90).length;
  const yellowReports = buildingFilteredReports.filter(r => r.days_since_report >= 60 && r.days_since_report < 90).length;
  const blueReports = buildingFilteredReports.filter(r => r.days_since_report >= 45 && r.days_since_report < 60).length;
  const greenReports = buildingFilteredReports.filter(r => r.days_since_report < 45).length;

  // Generate dashboard summary
  const generateDashboardSummary = () => {
    const totalPendingReports = buildingFilteredReports.length;
    const uniqueIndividuals = new Set(buildingFilteredReports.map(r => r.individual_id)).size;
    
    // Group by year
    const formsByYear = buildingFilteredReports.reduce((acc, report) => {
      const year = report.cycle_year || 'Unknown';
      if (!acc[year]) acc[year] = [];
      acc[year].push(report);
      return acc;
    }, {});
    
    const years = Object.keys(formsByYear).sort((a, b) => b - a);
    const currentYear = new Date().getFullYear();
    
    // Get most overdue report
    const mostOverdue = buildingFilteredReports.reduce((max, report) => 
      report.days_since_report > max.days_since_report ? report : max,
      buildingFilteredReports[0] || { days_since_report: 0 }
    );
    
    let mainMessage = '';
    let mood = 'neutral';
    
    if (totalPendingReports === 0) {
      // Check if this location has ANY individuals by looking at raw data
      const hasIndividualsAtLocation = selectedBuilding && selectedBuilding.name !== "All"
        ? processedReportData.some(r => r.building_location_name === selectedBuilding.name)
        : processedReportData.length > 0;

      if (selectedBuilding && selectedBuilding.name !== "All" && !hasIndividualsAtLocation) {
        // No individuals at this location - suggest switching
        mainMessage = `No individuals are assigned to ${selectedBuilding.name}. Try switching to "All" in the location dropdown to see all individuals.`;
        mood = 'neutral';
      } else {
        mainMessage = `All individuals have completed their forms${selectedBuilding && selectedBuilding.name !== "All" ? ` at ${selectedBuilding.name}` : ''}. No pending forms.`;
        mood = 'celebration';
      }
    } else if (redReports > 0) {
      const percentage = Math.round((redReports / totalPendingReports) * 100);
      mainMessage = `We have ${totalPendingReports} pending form${totalPendingReports > 1 ? 's' : ''} from ${uniqueIndividuals} individual${uniqueIndividuals > 1 ? 's' : ''}${selectedBuilding && selectedBuilding.name !== "All" ? ` at ${selectedBuilding.name}` : ''}. `;
      
      // Add year breakdown
      if (years.length > 1) {
        mainMessage += `Forms span ${years.length} years: `;
        years.forEach((year, idx) => {
          const count = formsByYear[year].length;
          mainMessage += `${count} from ${year}${idx < years.length - 1 ? ', ' : '. '}`;
        });
      }
      
      mainMessage += `${redReports} form${redReports > 1 ? 's are' : ' is'} over 90 days overdue. `;
      if (mostOverdue && mostOverdue.individual_name) {
        mainMessage += `The most overdue is ${mostOverdue.individual_name.split(' ')[0]}'s ${mostOverdue.cycle_year} ${mostOverdue.report_name} at ${mostOverdue.days_since_report} days.`;
      }
      mood = 'urgent';
    } else if (yellowReports > 0) {
      mainMessage = `${uniqueIndividuals} individual${uniqueIndividuals > 1 ? 's' : ''} ${uniqueIndividuals > 1 ? 'have' : 'has'} ${totalPendingReports} form${totalPendingReports > 1 ? 's' : ''} pending${selectedBuilding && selectedBuilding.name !== "All" ? ` at ${selectedBuilding.name}` : ''}. `;
      
      // Add year info if multiple years
      if (years.length > 1) {
        const priorYearCount = formsByYear[years.find(y => y != currentYear)]?.length || 0;
        if (priorYearCount > 0) {
          mainMessage += `${priorYearCount} form${priorYearCount > 1 ? 's are' : ' is'} from prior years. `;
        }
      }
      
      mainMessage += `${yellowReports} form${yellowReports > 1 ? 's are' : ' is'} between 60-90 days overdue.`;
      mood = 'warning';
    } else {
      mainMessage = `${uniqueIndividuals} individual${uniqueIndividuals > 1 ? 's' : ''} ${uniqueIndividuals > 1 ? 'have' : 'has'} ${totalPendingReports} form${totalPendingReports > 1 ? 's' : ''} pending${selectedBuilding && selectedBuilding.name !== "All" ? ` at ${selectedBuilding.name}` : ''}. `;
      
      if (years.length > 1 && formsByYear[currentYear]) {
        mainMessage += `${formsByYear[currentYear].length} from this year. `;
      }
      
      if (greenReports === totalPendingReports) {
        mainMessage += `All pending forms are under 45 days overdue.`;
      } else {
        mainMessage += `${greenReports} pending forms are under 45 days overdue.`;
      }
      mood = 'positive';
    }
    
    // Check if we should show location switching tip
    const showLocationTip = selectedBuilding && selectedBuilding.name !== "All" && totalPendingReports === 0 &&
      !processedReportData.some(r => r.building_location_name === selectedBuilding.name);

    return {
      mainMessage,
      mood,
      showLocationTip,
      stats: {
        totalPending: totalPendingReports,
        individuals: uniqueIndividuals,
        red: redReports,
        yellow: yellowReports,
        blue: blueReports,
        green: greenReports
      }
    };
  };

  const dashboardSummary = generateDashboardSummary();

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <Box>
      <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4">
            DASHBOARD
          </Typography>
          {selectedBuilding && selectedBuilding.name !== "All" && (
            <Chip 
              icon={<LocationIcon />} 
              label={`Filtered by: ${selectedBuilding.name}`}
              color="primary"
              variant="filled"
              sx={{ 
                fontWeight: 'medium',
                color: 'white',
                backgroundColor: '#0C5085',
                '& .MuiSvgIcon-root': {
                  color: 'white'
                }
              }}
            />
          )}
        </Box>
        
        {/* Dashboard Summary HUD */}
        <Card sx={{ 
          mb: 4, 
          bgcolor: dashboardSummary.mood === 'urgent' ? '#fff3cd' : 
                   dashboardSummary.mood === 'celebration' ? '#d4edda' : 
                   dashboardSummary.mood === 'warning' ? '#fff3cd' : '#e8f4fd',
          borderLeft: `6px solid ${
            dashboardSummary.mood === 'urgent' ? '#dc3545' : 
            dashboardSummary.mood === 'celebration' ? '#28a745' : 
            dashboardSummary.mood === 'warning' ? '#ffc107' : '#17a2b8'
          }`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              mb: 2,
              color: dashboardSummary.mood === 'urgent' ? '#721c24' : 
                     dashboardSummary.mood === 'celebration' ? '#155724' : 
                     dashboardSummary.mood === 'warning' ? '#856404' : '#0c5460'
            }}>
              {dashboardSummary.mainMessage}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
              <Chip 
                icon={<InfoIcon />}
                label={`${dashboardSummary.stats.totalPending} Pending Forms`}
                color="default"
                variant="outlined"
              />
              <Chip 
                label={`${dashboardSummary.stats.individuals} Individuals`}
                color="primary"
                variant="outlined"
              />
              {dashboardSummary.stats.green > 0 && (
                <Chip 
                  label={`${dashboardSummary.stats.green} Current`}
                  sx={{ bgcolor: 'lightgreen', color: 'text.primary' }}
                />
              )}
              {dashboardSummary.stats.blue > 0 && (
                <Chip 
                  label={`${dashboardSummary.stats.blue} Approaching`}
                  sx={{ bgcolor: 'lightblue', color: 'text.primary' }}
                />
              )}
              {dashboardSummary.stats.yellow > 0 && (
                <Chip 
                  label={`${dashboardSummary.stats.yellow} Due Soon`}
                  sx={{ bgcolor: '#E0E000', color: 'text.primary' }}
                />
              )}
              {dashboardSummary.stats.red > 0 && (
                <Chip 
                  label={`${dashboardSummary.stats.red} Overdue`}
                  sx={{ bgcolor: '#F55F5F', color: 'white' }}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Location tip when no individuals at selected location */}
        {dashboardSummary.showLocationTip && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            icon={<LocationIcon />}
          >
            <Typography variant="body2">
              <strong>Tip:</strong> The selected location "{selectedBuilding?.name}" has no individuals assigned.
              Use the location dropdown in the navigation bar to switch to <strong>"All"</strong> or select a different location to see individuals.
            </Typography>
          </Alert>
        )}

        {/* Timeline visualization */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={1} alignItems="center" justifyContent="space-between">
            <Grid item xs={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                bgcolor: 'lightgreen',
                mx: 'auto',
                mb: 1
              }} />
              <Typography variant="body2">Day 1</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                bgcolor: 'lightblue',
                mx: 'auto',
                mb: 1
              }} />
              <Typography variant="body2">45 Days</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                bgcolor: '#E0E000',
                mx: 'auto',
                mb: 1
              }} />
              <Typography variant="body2">60 Days</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                bgcolor: '#F55F5F',
                mx: 'auto',
                mb: 1
              }} />
              <Typography variant="body2">90 Days</Typography>
            </Grid>
          </Grid>
          
          <LinearProgress 
            variant="determinate" 
            value={100} 
            sx={{ 
              height: 8, 
              borderRadius: 2,
              mt: 1,
              mb: 2,
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(to right, lightgreen 25%, lightblue 25%, lightblue 50%, #E0E000 50%, #E0E000 75%, #F55F5F 75%)'
              }
            }} 
          />
        </Box>
        
        {/* Stats cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Current</Typography>
                    <Typography variant="h4">{greenReports}</Typography>
                  </Box>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    bgcolor: 'lightgreen',
                  }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Approaching</Typography>
                    <Typography variant="h4">{blueReports}</Typography>
                  </Box>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    bgcolor: 'lightblue',
                  }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Due Soon</Typography>
                    <Typography variant="h4">{yellowReports}</Typography>
                  </Box>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    bgcolor: '#E0E000',
                  }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Overdue</Typography>
                    <Typography variant="h4">{redReports}</Typography>
                  </Box>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    bgcolor: '#F55F5F',
                  }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Typography variant="h6" gutterBottom>
          Filter by Date Range
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: { xs: 'center', sm: 'flex-start' },
          overflow: 'auto',
          mb: 3, 
          mt: 2 
        }}>
          <ToggleButtonGroup
            value={filteredDays}
            exclusive
            onChange={handleFilterChange}
            aria-label="Day Range Filter"
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value={30}>30 Days</ToggleButton>
            <ToggleButton value={60}>60 Days</ToggleButton>
            <ToggleButton value={90}>90 Days</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Filters */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2 
        }}>
          <TextField
            label="Filter by name"
            variant="outlined"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            fullWidth
            size={isMobile ? "small" : "medium"}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />
          <TextField
            label="Filter by report"
            variant="outlined"
            value={filterReport}
            onChange={(e) => setFilterReport(e.target.value)}
            fullWidth
            size={isMobile ? "small" : "medium"}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />
        </Box>

        {/* Report Table */}
        <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
          Pending Forms (Individuals with Uncollected Forms)
        </Typography>
        
        {/* Mobile Card View */}
        {isMobile ? (
          <Stack spacing={2}>
            {isLoading ? (
              <Card>
                <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </CardContent>
              </Card>
            ) : filteredReports.length === 0 ? (
              <Card>
                <CardContent>
                  <Typography align="center">No reports found</Typography>
                </CardContent>
              </Card>
            ) : (
              filteredReports.map((report) => (
                <Card 
                  key={report.id}
                  component={Link}
                  to={`/people/${report.individual_id}?year=${report.cycle_year}`}
                  sx={{ 
                    textDecoration: 'none',
                    '&:hover': { boxShadow: 3 }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" color="primary">
                          {report.individual_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {report.report_name}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        backgroundColor: getStatusColor(report),
                        borderRadius: '50%',
                      }} />
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          <LocationIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                          Location
                        </Typography>
                        <Typography variant="body2">
                          {report.building_location_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          <ScheduleIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                          Days Overdue
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {report.days_since_report} days
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Year
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip 
                            label={report.cycle_year || 'Unknown'} 
                            size="small"
                            color={report.cycle_year === new Date().getFullYear() ? 'primary' : 'default'}
                            variant={report.cycle_year === new Date().getFullYear() ? 'filled' : 'outlined'}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Document Date
                        </Typography>
                        <Typography variant="body2">
                          {formatDateDisplay(report.due_date) || 'No document date'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))
            )}
          </Stack>
        ) : (
          /* Desktop Table View */
          <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig?.key === 'individual_name'}
                    direction={sortConfig?.key === 'individual_name' ? sortConfig.direction : 'asc'}
                    onClick={() => requestSort('individual_name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig?.key === 'report_name'}
                    direction={sortConfig?.key === 'report_name' ? sortConfig.direction : 'asc'}
                    onClick={() => requestSort('report_name')}
                  >
                    Report
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig?.key === 'building_location_name'}
                    direction={sortConfig?.key === 'building_location_name' ? sortConfig.direction : 'asc'}
                    onClick={() => requestSort('building_location_name')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig?.key === 'cycle_year'}
                    direction={sortConfig?.key === 'cycle_year' ? sortConfig.direction : 'asc'}
                    onClick={() => requestSort('cycle_year')}
                  >
                    Year
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig?.key === 'report_date'}
                    direction={sortConfig?.key === 'report_date' ? sortConfig.direction : 'asc'}
                    onClick={() => requestSort('report_date')}
                  >
                    Document Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig?.key === 'days_since_report'}
                    direction={sortConfig?.key === 'days_since_report' ? sortConfig.direction : 'asc'}
                    onClick={() => requestSort('days_since_report')}
                  >
                    Days Overdue
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No reports found
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((report) => (
                  <TableRow
                    key={report.id}
                    component={Link}
                    to={`/people/${report.individual_id}?year=${report.cycle_year}`}
                    sx={{ 
                      textDecoration: 'none', 
                      color: 'inherit',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      } 
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: getStatusColor(report),
                          borderRadius: '50%',
                          mx: 'auto',
                        }}
                      />
                    </TableCell>
                    <TableCell>{report.individual_name}</TableCell>
                    <TableCell>{report.report_name}</TableCell>
                    <TableCell>{report.building_location_name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={report.cycle_year || 'Unknown'} 
                        size="small"
                        color={report.cycle_year === new Date().getFullYear() ? 'primary' : 'default'}
                        variant={report.cycle_year === new Date().getFullYear() ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateDisplay(report.due_date) || 'No document date'}
                    </TableCell>
                    <TableCell>
                      {report.days_since_report} days
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;